import { useMemo } from "react";
import PageBanner from "@/components/feature/PageBanner";
import { SkeletonCard } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import type { VoteBrief } from "@/lib/types";
import VoteCard from "./components/VoteCard";
import { STATE_ORDER } from "./utils";

// /vote — 투표·설문 목록 (진행중 → 예정 → 종료)
export default function VotePage() {
  const { data, loading, error, reload, serverTs } = useApi<VoteBrief[]>("votes.php");
  const now = useNow();

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;

  const items = useMemo(() => {
    const list = data ?? [];
    return [...list].sort(
      (a, b) => (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9) || b.no - a.no
    );
  }, [data]);

  return (
    <>
      <PageBanner
        title="투표·설문"
        description="진행 중인 투표와 설문에 참여하고 결과를 확인하세요."
        breadcrumb={[{ label: "투표·설문" }]}
      />

      <section className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
        {loading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="투표·설문을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon="ri-bar-chart-box-line"
            title="진행 중인 투표·설문이 없습니다"
            description="새 투표가 등록되면 이곳에 표시됩니다."
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((vote) => (
              <VoteCard
                key={vote.no}
                vote={vote}
                remaining={
                  vote.state === "open"
                    ? Math.max(0, vote.end_in - elapsed)
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}